import { useState } from 'react';
import { api } from '../../lib/api';
import { extractApiErrorMessage } from '../../lib/api-error';

type CrmSourceType =
  | 'perfex_lead'
  | 'perfex_customer'
  | 'perfex_contact'
  | 'perfex_cold_lead'
  | 'manual';

type Mode = 'single' | 'range';

interface SinglePreview {
  callCount: number;
  succeededAnalyses: number;
  deletableAnalyses: number;
  callsWithExtraVersions: number;
  hasRecordSummary: boolean;
}

interface SingleResult extends SinglePreview {
  deletedAnalyses: number;
  recordSummaryDeleted: boolean;
  regenerated: boolean;
}

interface BulkPreview {
  callsInRange: number;
  callsWithMatchedLead: number;
  succeededAnalyses: number;
  deletableAnalyses: number;
  callsWithExtraVersions: number;
  distinctLeadsAffected: number;
  recordSummariesPresent: number;
}

interface BulkResult extends BulkPreview {
  deletedAnalyses: number;
  recordSummariesDeleted: number;
}

export function AiCleanupLeadSection(): JSX.Element {
  const [mode, setMode] = useState<Mode>('single');

  // Single-lead mode state
  const [crmSourceType, setCrmSourceType] = useState<CrmSourceType>('perfex_lead');
  const [crmSourceIdRaw, setCrmSourceIdRaw] = useState('');
  const [regenerate, setRegenerate] = useState(true);

  // Date-range mode state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Shared state
  const [previewBusy, setPreviewBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [singlePreview, setSinglePreview] = useState<SinglePreview | null>(null);
  const [singleResult, setSingleResult] = useState<SingleResult | null>(null);
  const [bulkPreview, setBulkPreview] = useState<BulkPreview | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  function parseId(): number | null {
    const n = Number(crmSourceIdRaw.trim());
    return Number.isInteger(n) && n > 0 ? n : null;
  }

  function clearResults(): void {
    setSinglePreview(null);
    setSingleResult(null);
    setBulkPreview(null);
    setBulkResult(null);
    setMsg(null);
  }

  function handleModeChange(next: Mode): void {
    if (next === mode) return;
    setMode(next);
    clearResults();
  }

  async function handleSinglePreview(): Promise<void> {
    const id = parseId();
    if (id === null) {
      setMsg({ ok: false, text: 'Enter a positive integer record ID.' });
      return;
    }
    setPreviewBusy(true);
    clearResults();
    try {
      const res = await api.post<SinglePreview>('/ai/cleanup-lead', {
        crmSourceType,
        crmSourceId: id,
        dryRun: true,
      });
      setSinglePreview(res);
    } catch (err) {
      setMsg({ ok: false, text: extractApiErrorMessage(err, 'Preview failed.') });
    } finally {
      setPreviewBusy(false);
    }
  }

  async function handleSingleRun(): Promise<void> {
    const id = parseId();
    if (id === null) {
      setMsg({ ok: false, text: 'Enter a positive integer record ID.' });
      return;
    }
    if (!singlePreview) {
      setMsg({ ok: false, text: 'Run preview first.' });
      return;
    }
    setRunBusy(true);
    setMsg(null);
    try {
      const res = await api.post<SingleResult>('/ai/cleanup-lead', {
        crmSourceType,
        crmSourceId: id,
        regenerate,
      });
      setSingleResult(res);
      setSinglePreview(null);
      const parts = [`deleted ${res.deletedAnalyses} stale analyses`];
      if (res.recordSummaryDeleted) parts.push('wiped rolling summary');
      if (res.regenerated) parts.push('regenerated and pushed to Perfex');
      else if (regenerate) parts.push('regenerate failed — check logs');
      setMsg({ ok: true, text: `Done — ${parts.join(', ')}.` });
    } catch (err) {
      setMsg({ ok: false, text: extractApiErrorMessage(err, 'Cleanup failed.') });
    } finally {
      setRunBusy(false);
    }
  }

  function buildRangePayload(): { dateFrom?: string; dateTo?: string } | null {
    const payload: { dateFrom?: string; dateTo?: string } = {};
    if (dateFrom) payload.dateFrom = new Date(dateFrom).toISOString();
    if (dateTo) {
      // Make `dateTo` inclusive — push to end-of-day in the user's local TZ.
      const d = new Date(dateTo);
      d.setHours(23, 59, 59, 999);
      payload.dateTo = d.toISOString();
    }
    if (!payload.dateFrom && !payload.dateTo) return null;
    return payload;
  }

  async function handleBulkPreview(): Promise<void> {
    const payload = buildRangePayload();
    if (!payload) {
      setMsg({ ok: false, text: 'Enter at least one of Date from / Date to.' });
      return;
    }
    setPreviewBusy(true);
    clearResults();
    try {
      const res = await api.post<BulkPreview>('/ai/cleanup-bulk', { ...payload, dryRun: true });
      setBulkPreview(res);
    } catch (err) {
      setMsg({ ok: false, text: extractApiErrorMessage(err, 'Preview failed.') });
    } finally {
      setPreviewBusy(false);
    }
  }

  async function handleBulkRun(): Promise<void> {
    const payload = buildRangePayload();
    if (!payload) {
      setMsg({ ok: false, text: 'Enter at least one of Date from / Date to.' });
      return;
    }
    if (!bulkPreview) {
      setMsg({ ok: false, text: 'Run preview first.' });
      return;
    }
    setRunBusy(true);
    setMsg(null);
    try {
      const res = await api.post<BulkResult>('/ai/cleanup-bulk', payload);
      setBulkResult(res);
      setBulkPreview(null);
      setMsg({
        ok: true,
        text: `Done — deleted ${res.deletedAnalyses} stale analyses, wiped ${res.recordSummariesDeleted} rolling summaries across ${res.distinctLeadsAffected} lead(s). Re-run analyze to rebuild summaries.`,
      });
    } catch (err) {
      setMsg({ ok: false, text: extractApiErrorMessage(err, 'Cleanup failed.') });
    } finally {
      setRunBusy(false);
    }
  }

  const handlePreview = mode === 'single' ? handleSinglePreview : handleBulkPreview;
  const handleRun = mode === 'single' ? handleSingleRun : handleBulkRun;
  const previewExists = mode === 'single' ? singlePreview !== null : bulkPreview !== null;
  const previewDeletable =
    mode === 'single' ? singlePreview?.deletableAnalyses ?? 0 : bulkPreview?.deletableAnalyses ?? 0;
  const previewSummariesToWipe =
    mode === 'single'
      ? singlePreview?.hasRecordSummary
        ? 1
        : 0
      : bulkPreview?.recordSummariesPresent ?? 0;
  const runDisabled =
    runBusy || !previewExists || (previewDeletable === 0 && previewSummariesToWipe === 0);

  return (
    <section className="card p-4 space-y-4">
      <header className="border-b border-ink-200 pb-3">
        <h2 className="text-sm font-semibold text-ink-900">Reset analyses</h2>
        <p className="text-xs text-ink-500 mt-0.5">
          Delete all but the latest succeeded analysis per call and wipe the rolling summary cache so it rebuilds
          cleanly. Use this when an old, wrong analysis is still polluting lead summaries. Failed and pending analyses
          are not touched.
        </p>
      </header>

      <div className="flex gap-2 text-xs">
        <button
          type="button"
          className={mode === 'single' ? 'btn-primary !text-xs' : 'btn-secondary !text-xs'}
          onClick={() => handleModeChange('single')}
        >
          Single lead
        </button>
        <button
          type="button"
          className={mode === 'range' ? 'btn-primary !text-xs' : 'btn-secondary !text-xs'}
          onClick={() => handleModeChange('range')}
        >
          Date range (bulk)
        </button>
      </div>

      {mode === 'single' && (
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className="label">Record type</label>
            <select
              className="input"
              value={crmSourceType}
              onChange={(e) => setCrmSourceType(e.target.value as CrmSourceType)}
            >
              <option value="perfex_lead">perfex_lead</option>
              <option value="perfex_customer">perfex_customer</option>
              <option value="perfex_contact">perfex_contact</option>
              <option value="perfex_cold_lead">perfex_cold_lead</option>
              <option value="manual">manual</option>
            </select>
          </div>
          <div>
            <label className="label">Record ID</label>
            <input
              className="input"
              inputMode="numeric"
              value={crmSourceIdRaw}
              onChange={(e) => setCrmSourceIdRaw(e.target.value)}
              placeholder="12345"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-ink-700">
              <input type="checkbox" checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} />
              Regenerate rolling summary after cleanup
            </label>
          </div>
        </div>
      )}

      {mode === 'range' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Date from</label>
              <input className="input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="label">Date to</label>
              <input className="input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
          <p className="text-[11px] text-ink-500">
            Removes stale analysis versions and clears rolling summary cache for every CRM-matched call in the range.
            No regeneration is performed — re-run <span className="font-mono">Bulk analyze</span> to rebuild
            summaries.
          </p>
        </>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn-secondary" onClick={() => void handlePreview()} disabled={previewBusy}>
          {previewBusy ? 'Checking…' : 'Preview'}
        </button>
        <button className="btn-primary" onClick={() => void handleRun()} disabled={runDisabled}>
          {runBusy ? 'Resetting…' : 'Reset'}
        </button>
      </div>

      {singlePreview && (
        <div className="rounded border border-ink-200 bg-ink-50 px-3 py-2 text-xs text-ink-800 space-y-0.5">
          <div>{singlePreview.callCount} call(s) for this record</div>
          <div>
            {singlePreview.succeededAnalyses} succeeded analyses · {singlePreview.callsWithExtraVersions} call(s) have
            extra versions · <span className="font-semibold">{singlePreview.deletableAnalyses}</span> would be deleted
          </div>
          <div>Rolling summary cached: {singlePreview.hasRecordSummary ? 'yes (will be wiped)' : 'no'}</div>
          {singlePreview.deletableAnalyses === 0 && !singlePreview.hasRecordSummary && (
            <div className="text-ink-500">Nothing to clean — every call already has at most one succeeded analysis.</div>
          )}
        </div>
      )}

      {bulkPreview && (
        <div className="rounded border border-ink-200 bg-ink-50 px-3 py-2 text-xs text-ink-800 space-y-0.5">
          <div>
            {bulkPreview.callsInRange} call(s) in range · {bulkPreview.callsWithMatchedLead} matched to a CRM record
          </div>
          <div>
            {bulkPreview.succeededAnalyses} succeeded analyses · {bulkPreview.callsWithExtraVersions} call(s) have
            extra versions · <span className="font-semibold">{bulkPreview.deletableAnalyses}</span> would be deleted
          </div>
          <div>
            Distinct leads affected: {bulkPreview.distinctLeadsAffected} · rolling summaries to wipe:{' '}
            <span className="font-semibold">{bulkPreview.recordSummariesPresent}</span>
          </div>
          {bulkPreview.deletableAnalyses === 0 && bulkPreview.recordSummariesPresent === 0 && (
            <div className="text-ink-500">
              Nothing to clean — no extra analysis versions and no rolling summaries cached for this range.
            </div>
          )}
        </div>
      )}

      {singleResult && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 space-y-0.5">
          <div>Deleted analyses: {singleResult.deletedAnalyses}</div>
          <div>Rolling summary wiped: {singleResult.recordSummaryDeleted ? 'yes' : 'no'}</div>
          <div>Regenerated: {singleResult.regenerated ? 'yes (pushed to Perfex)' : 'no'}</div>
        </div>
      )}

      {bulkResult && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 space-y-0.5">
          <div>Deleted analyses: {bulkResult.deletedAnalyses}</div>
          <div>Rolling summaries wiped: {bulkResult.recordSummariesDeleted}</div>
          <div>Distinct leads affected: {bulkResult.distinctLeadsAffected}</div>
        </div>
      )}

      {msg && <p className={`text-sm ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</p>}
    </section>
  );
}
